import { useData } from "../../runtime/provider";
import { FOR_YOU_INPUTS } from "@hooks/trpc/useForYou";
import { RECOMMENDED_ARTISTS_INPUTS } from "@hooks/trpc/useRecommendedArtists";
import { RECOMMENDED_PLAYLISTS_INPUTS } from "@hooks/trpc/useRecommendedPlaylists";
import * as rudderstack from "@utils/rudderstack";
import { trpc } from "@utils/trpc";
import { captureException } from "@utils/captureException";
import {
  LibraryQueryData,
  libraryQueryKey,
} from "@hooks/trpc/useLibrary/queryFunctions";
import { todaysTopHitsQueryKey } from "@hooks/trpc/useTodaysTopHits";
import type { PlaylistWithSongs } from "@libs/wire";

const getRemoveItemById =
  (id: string) =>
  <T extends { pages: { data: { id: string }[] }[] }>(data: T | undefined) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            data: page.data.filter((item) => item.id !== id),
          })),
        }
      : undefined;

/**
 * Mark a song, album, playlist, or artist as not interested in recommendations
 * Interacts with the different caches to immediately remove the item from the recommendations list
 */
export const useNotinterested = () => {
  const session = useData();
  const { queryClient } = session;
  const notInterestedMutation =
    trpc.user.markContentAsNotInterested.useMutation({
      onSettled: async (data, error) => {
        if (error) {
          console.error(error);
          return;
        } else if (data) {
          const { resourceType } = data;

          switch (resourceType) {
            case "album": {
              await session.utils.catalog.listForYouPaginated.refetch();
              break;
            }
            case "artist": {
              await session.utils.catalog.artists.listRecommendedArtistsPaginated.refetch();
              break;
            }
            case "playlist": {
              await Promise.all([
                session.utils.catalog.listForYouPaginated.refetch(),
                session.utils.catalog.playlists.listRecommendedPlaylistsPaginated.refetch(),
              ]);
              break;
            }
            case "song": {
              await Promise.all([
                queryClient.refetchQueries({
                  queryKey: libraryQueryKey(),
                }),
                queryClient.refetchQueries({
                  queryKey: todaysTopHitsQueryKey(),
                }),
              ]);
              break;
            }
          }
        }
      },
    });

  const mutation = ({
    id,
    type,
  }: {
    id: string;
    type: "song" | "album" | "playlist" | "artist";
  }) => {
    void rudderstack.track("not_interested", {
      resourceId: id,
      resourceType: type,
    });

    notInterestedMutation
      .mutateAsync({
        resourceId: id,
        resourceType: type,
      })
      .catch((error) => {
        captureException(error, {
          channel: "error",
          area: "recommendations",
          operation: "mark-not-interested",
          tags: { resourceId: id, resourceType: type },
        });
      });

    switch (type) {
      case "song": {
        // Remove from discover daily
        queryClient.setQueryData<LibraryQueryData>(libraryQueryKey(), (data) =>
          data?.map((x) => {
            if (x.type === "playlist" && x.id === "discover_daily") {
              return {
                ...x,
                songs: x.songIds.filter((x: string) => x !== id),
                privateSongIds:
                  x.privateSongIds?.filter((x: string) => x !== id) ?? [],
              };
            }
            return x;
          }),
        );
        // Remove from todays top hits — write through to durable cache.
        session.cache.setQueryDataDurable<PlaylistWithSongs>(
          queryClient,
          todaysTopHitsQueryKey(),
          (data) => {
            if (!data) return data;
            return {
              ...data,
              songs: data.songs.filter((x) => x.id !== id),
            };
          },
        );
        break;
      }
      case "album": {
        // Remove from forYou
        session.utils.catalog.listForYouPaginated.setInfiniteData(
          FOR_YOU_INPUTS,
          getRemoveItemById(id),
        );
        break;
      }
      case "playlist": {
        // Remove from forYou
        session.utils.catalog.listForYouPaginated.setInfiniteData(
          FOR_YOU_INPUTS,
          getRemoveItemById(id),
        );

        // Remove from recommendedPlaylists
        session.utils.catalog.playlists.listRecommendedPlaylistsPaginated.setInfiniteData(
          RECOMMENDED_PLAYLISTS_INPUTS,
          getRemoveItemById(id),
        );
        session.utils.catalog.playlists.listRecommendedPlaylists.setData(
          undefined,
          (data) => data?.filter((x) => x.id !== id),
        );
        break;
      }
      case "artist": {
        // Remove from recommendedArtists
        session.utils.catalog.artists.listRecommendedArtistsPaginated.setInfiniteData(
          RECOMMENDED_ARTISTS_INPUTS,
          getRemoveItemById(id),
        );
        break;
      }
    }
  };

  return mutation;
};
